/**
 * Reliable timezone detection with multiple fallback methods
 * @returns {Promise<string>} IANA timezone identifier (e.g., 'Europe/Moscow', 'America/New_York')
 */
export const detectTimezone = async () => {
  // 1. Try browser Intl API first (most accurate - reflects user's system settings)
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz && typeof tz === 'string' && tz.length > 0) {
      return tz;
    }
  } catch {
    // Intl not available or failed
  }

  // 2. Try to calculate from timezone offset (works everywhere but less precise)
  try {
    const offset = -new Date().getTimezoneOffset();
    // Etc/GMT names carry whole hours only, so the minutes part has nowhere to go.
    const hours = Math.abs(Math.floor(offset / 60));
    const sign = offset >= 0 ? '+' : '-';
    return `Etc/GMT${sign}${hours}`;
  } catch {
    // Date methods failed
  }

  // 3. Try GeoIP service as fallback (async)
  try {
    const response = await fetch('https://ipapi.co/json/', {
      signal: AbortSignal.timeout(5000), // 5 second timeout
    });
    if (response.ok) {
      const data = await response.json();
      if (data.timezone && typeof data.timezone === 'string') {
        return data.timezone;
      }
    }
  } catch {
    // GeoIP failed or timed out
  }

  // 4. Final fallback
  return 'UTC';
};

/**
 * Synchronous version of timezone detection (no GeoIP fallback)
 * @returns {string} IANA timezone identifier
 */
export const detectTimezoneSync = () => {
  // 1. Try browser Intl API
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz && typeof tz === 'string' && tz.length > 0) {
      return tz;
    }
  } catch {
    // Intl not available
  }

  // 2. Calculate from offset
  try {
    const offset = -new Date().getTimezoneOffset();
    // Etc/GMT names carry whole hours only, so the minutes part has nowhere to go.
    const hours = Math.abs(Math.floor(offset / 60));
    const sign = offset >= 0 ? '+' : '-';
    return `Etc/GMT${sign}${hours}`;
  } catch {
    // Date methods failed
  }

  // 3. Fallback
  return 'UTC';
};
